import type { ReactNode, TextareaHTMLAttributes } from 'react'
import { controlClassName } from './Field'

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly id: string
  readonly label: string
  /** Rendered under the control and wired via aria-describedby. */
  readonly hint?: ReactNode
  /** Rendered as an error and wired via aria-describedby + aria-invalid. */
  readonly error?: string
}

/**
 * `Field`, for a `<textarea>`.
 *
 * Same wiring as `Field.tsx` and for the same reason: the `htmlFor`, `aria-describedby`
 * and `aria-invalid` between label, control, hint and error are what make a form usable
 * with a screen reader, and they are what gets forgotten when the pieces are separate.
 * The only differences are the element and its height.
 */
export function Textarea({
  id,
  label,
  hint,
  error,
  className,
  ...props
}: TextareaProps) {
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <textarea
        id={id}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        className={controlClassName(
          error,
          `min-h-40 resize-y px-3 py-2.5 leading-relaxed ${className ?? ''}`.trim(),
        )}
        {...props}
      />
      {hint ? (
        <p id={hintId} className="text-sm text-ink-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}
